<template>
  <b-modal
    v-model="showModal"
    title="Loans Report"
    size="xl"
    centered
    no-close-on-backdrop
    @shown="onModalShown"
  >
    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div
        v-if="typeof loadingError === 'object'"
        class="alert-body"
      >
        <p
          v-for="(message, index) in loadingError"
          :key="index"
        >
          {{ message }}
        </p>
      </div>
      <p
        v-else
        class="alert-body"
      >
        {{ loadingError }}
      </p>
    </b-alert>

    <div class="mb-2 d-flex justify-content-between align-items-center">
      <div>
        <strong>Date Range:</strong>
        {{ dateRange ? formatDateRange(dateRange) : 'All Time' }}
      </div>

      <b-button
        variant="outline-primary"
        :disabled="loading || printing"
        @click="handlePrint"
      >
        <b-spinner
          v-if="printing"
          small
          class="mr-1"
        />
        <feather-icon
          v-else
          icon="PrinterIcon"
          class="mr-1"
        />
        Print
      </b-button>
    </div>

    <div
      v-if="loading"
      class="text-center my-5"
    >
      <b-spinner
        label="Loading..."
        variant="primary"
      />
    </div>

    <div v-else-if="!loadingError">
      <div class="table-responsive">
        <table class="table table-bordered table-sm compact-table">
          <thead>
            <tr class="bg-light">
              <th style="width: 4%; border: 1px solid #808080;">
                #
              </th>
              <th style="width: 20%; border: 1px solid #808080;">
                Borrower
              </th>
              <th
                style="width: 11%; border: 1px solid #808080;"
                class="text-right"
              >
                Amount (₹)
              </th>
              <th style="width: 7%; border: 1px solid #808080;">
                Status
              </th>
              <th style="width: 20%; border: 1px solid #808080;">
                Remarks
              </th>
              <th style="width: 12%; border: 1px solid #808080;">
                Taken
              </th>
              <th style="width: 14%; border: 1px solid #808080;">
                Repayment Mode
              </th>
              <th style="width: 12%; border: 1px solid #808080;">
                Repayment
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rowIndex) in flattenedRows"
              :key="`row-${rowIndex}`"
              :class="row.type === 'total' ? 'borrower-total-row' : 'borrower-group-row'"
            >
              <template v-if="row.type === 'loan'">
                <td
                  v-if="rowSpanData[rowIndex]"
                  :rowspan="rowSpanData[rowIndex]"
                  class="text-center"
                  style="vertical-align: middle;"
                >
                  {{ row.srNo }}
                </td>
                <td
                  v-if="rowSpanData[rowIndex]"
                  :rowspan="rowSpanData[rowIndex]"
                  style="vertical-align: middle;"
                >
                  <div style="word-break: break-word; white-space: normal;">
                    <span v-if="row.data.borrower_contact_no || row.data.mr_mobile">
                      {{ row.data.borrower_name || row.data.mr_name }} - {{ row.data.borrower_contact_no || row.data.mr_mobile }}
                    </span>
                    <span v-else>
                      {{ row.data.borrower_name || row.data.mr_name }}
                    </span>
                  </div>
                </td>
                <td class="text-right">
                  {{ formatAmount(row.data.amount) }}
                </td>
                <td>
                  <b-badge
                    :variant="row.data.status === 'open' ? 'warning' : 'success'"
                    class="text-capitalize"
                  >
                    {{ row.data.status }}
                  </b-badge>
                </td>
                <td>
                  <div style="word-break: break-word; white-space: normal;">
                    {{ row.data.remarks || '-' }}
                  </div>
                </td>
                <td>
                  <div style="font-size: 0.75rem;">
                    <div>{{ row.data.taken_at ? formatedDate(row.data.taken_at, 'DD-MM-YYYY LT') : '-' }}</div>
                    <div class="text-primary">
                      {{ row.data.created_by_name || '-' }}
                    </div>
                  </div>
                </td>
                <td>
                  <div style="word-break: break-word; white-space: normal;">
                    {{ row.data.repayment_mode ? getRepaymentModeName(row.data.repayment_mode) : '-' }}
                  </div>
                </td>
                <td>
                  <div style="font-size: 0.75rem;">
                    <div>{{ row.data.repayed_at ? formatedDate(row.data.repayed_at, 'DD-MM-YYYY LT') : '-' }}</div>
                    <div class="text-primary">
                      {{ row.data.repayment_received_by_name || '-' }}
                    </div>
                  </div>
                </td>
              </template>
              <template v-else>
                <td
                  colspan="2"
                  class="text-right font-weight-bold"
                >
                  Subtotal:
                </td>
                <td class="text-right font-weight-bold">
                  {{ formatAmount(row.total) }}
                </td>
                <td colspan="5" />
              </template>
            </tr>
          </tbody>

          <!-- Grand Total Footer -->
          <tfoot class="table-footer-highlight">
            <tr>
              <td
                colspan="2"
                class="text-right font-weight-bold"
              >
                Grand Total:
              </td>
              <td class="text-right font-weight-bold">
                ₹{{ formatAmount(grandTotal) }}
              </td>
              <td colspan="5" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button
          variant="secondary"
          @click="showModal=false"
        >
          Close
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BAlert, BButton, BBadge,
} from 'bootstrap-vue'
import dateMixin from '@/mixins/dateMixin'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BModal,
    BSpinner,
    BAlert,
    BButton,
    BBadge,
  },
  mixins: [dateMixin],
  props: {
    value: { type: Boolean, default: false },
    loansData: { type: Array, default: () => [] },
    dateRange: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      printing: false,
      loadingError: null,
      repaymentModeOptions: [
        { value: 'cash', text: 'Cash' },
        { value: 'upi', text: 'UPI' },
        { value: 'cheque', text: 'Cheque' },
        { value: 'rgts', text: 'RGTS' },
        { value: 'credit_note', text: 'Credit Note' },
        { value: 'transferred_to_salary_advance', text: 'Trans. to Sal. Adv' },
        { value: 'transferred_to_medicine_advance', text: 'Trans. to Med. Adv' },
      ],
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      },
    },
    groupedLoans() {
      // Group loans by borrower
      const groups = {}
      this.loansData.forEach(loan => {
        const borrowerKey = `${loan.borrower_name || loan.mr_name}_${loan.borrower_contact_no || loan.mr_mobile || ''}`

        if (!groups[borrowerKey]) {
          groups[borrowerKey] = {
            borrowerName: loan.borrower_contact_no || loan.mr_mobile
              ? `${loan.borrower_name || loan.mr_name} - ${loan.borrower_contact_no || loan.mr_mobile}`
              : loan.borrower_name || loan.mr_name,
            loans: [],
            total: 0,
          }
        }

        groups[borrowerKey].loans.push(loan)
        groups[borrowerKey].total += parseFloat(loan.amount || 0)
      })

      // Sort loans within each group by remarks
      Object.values(groups).forEach(group => {
        group.loans.sort((a, b) => {
          const remarkA = (a.remarks || '').trim()
          const remarkB = (b.remarks || '').trim()

          // Helper function to categorize remarks
          const getCategoryAndValue = remark => {
            if (!remark) return { category: 3, value: '' } // Empty remarks go last

            const upperRemark = remark.toUpperCase()

            // Check if starts with V followed by digits
            if (/^V\d/.test(upperRemark)) {
              return { category: 0, value: remark }
            }

            // Check if starts with S followed by letters (like SLCL)
            if (/^S[A-Z]/.test(upperRemark)) {
              return { category: 1, value: remark }
            }

            // All other remarks (alphabetical)
            return { category: 2, value: remark }
          }

          const catA = getCategoryAndValue(remarkA)
          const catB = getCategoryAndValue(remarkB)

          // First, sort by category
          if (catA.category !== catB.category) {
            return catA.category - catB.category
          }
          // Within same category, sort based on category type
          if (catA.category === 0 || catA.category === 1) {
            // For V and S categories: sort in DESCENDING order (reverse)
            return catB.value.localeCompare(catA.value, undefined, { sensitivity: 'base' })
          }
          // For other categories: sort in ASCENDING order (normal)
          return catA.value.localeCompare(catB.value, undefined, { sensitivity: 'base' })
        })
      })

      // Convert to array and sort by borrower name
      return Object.values(groups).sort((a, b) => a.borrowerName.localeCompare(b.borrowerName))
    },
    grandTotal() {
      return this.loansData.reduce((sum, loan) => sum + parseFloat(loan.amount || 0), 0)
    },
    flattenedRows() {
      const rows = []
      let srNo = 1

      this.groupedLoans.forEach(group => {
        // Add all loan rows for this borrower
        group.loans.forEach(loan => {
          rows.push({
            type: 'loan',
            data: loan,
            srNo,
          })
        })

        // Add total row for this borrower
        rows.push({
          type: 'total',
          borrowerName: group.borrowerName,
          total: group.total,
        })

        srNo += 1
      })

      return rows
    },
    rowSpanData() {
      // Calculate rowspan for borrower names (merge cells for same borrower)
      const spanData = {}
      let currentBorrower = null
      let spanCount = 0
      let startIndex = 0

      this.flattenedRows.forEach((row, index) => {
        if (row.type === 'loan') {
          const borrowerKey = row.borrower_contact_no || row.mr_mobile
            ? `${row.data.borrower_name || row.data.mr_name}_${row.borrower_contact_no || row.mr_mobile}`
            : row.data.borrower_name || row.data.mr_name

          if (currentBorrower !== borrowerKey) {
            // Save previous borrower's span
            if (currentBorrower !== null) {
              spanData[startIndex] = spanCount
            }
            // Start new borrower
            currentBorrower = borrowerKey
            spanCount = 1
            startIndex = index
          } else {
            spanCount += 1
          }
        } else if (row.type === 'total') {
          // Save last borrower's span before total row
          if (currentBorrower !== null) {
            spanData[startIndex] = spanCount
          }
          currentBorrower = null
          spanCount = 0
        }
      })

      return spanData
    },
  },
  methods: {
    onModalShown() {
      // Modal is shown with data passed from parent
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    formatDateRange(range) {
      if (!range) return 'All Time'
      const [startDate, endDate] = range.split(' to ')
      if (startDate && endDate) {
        return `${this.formatedDate(startDate, 'DD-MM-YYYY')} to ${this.formatedDate(endDate, 'DD-MM-YYYY')}`
      }
      return range
    },
    getRepaymentModeName(mode) {
      const repaymentMode = this.repaymentModeOptions.find(m => m.value === mode)
      return repaymentMode ? repaymentMode.text : mode
    },
    async handlePrint() {
      try {
        this.printing = true
        await this.printLoansReport()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.message || 'Failed to print!',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.printing = false
      }
    },
    printLoansReport() {
      return new Promise((resolve, reject) => {
        try {
          const dateRangeText = this.dateRange ? this.formatDateRange(this.dateRange) : 'All Time'

          // Build summary section
          const summaryHTML = `
  <div style="margin-bottom: 20px; padding: 15px; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px;">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
      <h2 style="margin: 0; font-size: 20px;">Loans Report</h2>
      <div style="font-size: 14px; font-weight: bold;">Date Range: ${dateRangeText}</div>
    </div>
    <div style="display: flex; gap: 30px; flex-wrap: wrap;">
      <div><strong>Total Borrowers:</strong> ${this.groupedLoans.length}</div>
      <div><strong>Total Loans:</strong> ${this.loansData.length}</div>
      <div><strong>Grand Total Amount:</strong> ₹${this.formatAmount(this.grandTotal)}</div>
    </div>
  </div>
`

          // Build table HTML with proper borders and wrapping
          let tableHTML = '<table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; table-layout: fixed;">'

          // Add header row
          tableHTML += '<thead><tr>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: center; width: 3%;">#</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: left; width: 16%;">Borrower</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: right; width: 13%;">Amount (₹)</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: center; width: 6%;">Status</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: left; width: 20%;">Remarks</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: left; width: 13%;">Taken</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: left; width: 13%;">Repayment Mode</th>'
          tableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 10px; border: 1px solid #808080; padding: 6px; text-align: left; width: 13%;">Repayment</th>'
          tableHTML += '</tr></thead>'

          // Add data rows grouped by borrower
          tableHTML += '<tbody>'
          let srNo = 1
          this.groupedLoans.forEach(group => {
            const groupRowCount = group.loans.length

            group.loans.forEach((loan, index) => {
              const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f8f8'
              const borrowerName = loan.borrower_contact_no || loan.mr_mobile
                ? `${loan.borrower_name || loan.mr_name} - ${loan.borrower_contact_no || loan.mr_mobile}`
                : loan.borrower_name || loan.mr_name

              // First row of group gets top border (2px gray)
              const topBorder = index === 0 ? '2px solid #808080' : '1px solid #808080'

              tableHTML += `<tr style="border-top: ${topBorder};">`

              // Sr. No - only show in first row of group (merged cell)
              if (index === 0) {
                tableHTML += `<td rowspan="${groupRowCount}" style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 9px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: center; vertical-align: middle;">${srNo}</td>`
              }

              // Borrower - only show in first row of group (merged cell)
              if (index === 0) {
                tableHTML += `<td rowspan="${groupRowCount}" style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 9px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: left; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; vertical-align: middle;">${borrowerName}</td>`
              }

              // Amount
              tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 9px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: right; white-space: nowrap;">${this.formatAmount(loan.amount)}</td>`

              // Status
              const statusColor = loan.status === 'open' ? '#ffc107' : '#28a745'
              tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 9px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: center;"><span style="background-color: ${statusColor}; color: white; padding: 2px 6px; border-radius: 3px; text-transform: capitalize; font-size: 8px; white-space: nowrap;">${loan.status}</span></td>`

              // Remarks
              tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 9px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: left; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal;">${loan.remarks || '-'}</td>`

              // Taken
              const takenAt = loan.taken_at ? this.formatedDate(loan.taken_at, 'DD-MM-YYYY LT') : '-'
              const createdBy = loan.created_by_name || '-'
              tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 8px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: left; word-wrap: break-word; overflow-wrap: break-word;">${takenAt}<br><span style="color: #007bff;">${createdBy}</span></td>`

              // Repayment Mode
              const repaymentMode = loan.repayment_mode ? this.getRepaymentModeName(loan.repayment_mode) : '-'
              tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 9px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: left; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal;">${repaymentMode}</td>`

              // Repayment
              const repayedAt = loan.repayed_at ? this.formatedDate(loan.repayed_at, 'DD-MM-YYYY LT') : '-'
              const repaymentReceivedBy = loan.repayment_received_by_name || '-'
              tableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 8px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: 1px solid #808080; border-top: ${topBorder}; padding: 4px; text-align: left; word-wrap: break-word; overflow-wrap: break-word;">${repayedAt}<br><span style="color: #007bff;">${repaymentReceivedBy}</span></td>`

              tableHTML += '</tr>'
            })

            // Borrower Total Row with bottom border (2px gray) for group
            const groupBottomBorder = '2px solid #808080'
            tableHTML += `<tr style="border-bottom: ${groupBottomBorder};">`
            tableHTML += `<td colspan="2" style="background-color: #e3f2fd !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: ${groupBottomBorder}; border-top: 1px solid #808080; padding: 4px; text-align: right; font-weight: bold;">Subtotal:</td>`
            tableHTML += `<td style="background-color: #e3f2fd !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: ${groupBottomBorder}; border-top: 1px solid #808080; padding: 4px; text-align: right; font-weight: bold; white-space: nowrap;">${this.formatAmount(group.total)}</td>`
            tableHTML += `<td colspan="5" style="background-color: #e3f2fd !important; -webkit-print-color-adjust: exact; color-adjust: exact; border-left: 1px solid #808080; border-right: 1px solid #808080; border-bottom: ${groupBottomBorder}; border-top: 1px solid #808080;"></td>`
            tableHTML += '</tr>'

            srNo += 1
          })
          tableHTML += '</tbody>'
          tableHTML += '</table>'

          // Grand Total as separate table after main table - no tfoot
          const grandTotalHTML = `
<table style="width: 100%; border-collapse: collapse; margin-top: 10px; table-layout: fixed;">
  <tr>
    <td colspan="2" style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 12px; border: 2px solid #808080; padding: 8px; text-align: right; font-weight: bold; width: 19%;">Grand Total:</td>
    <td style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 12px; border: 2px solid #808080; padding: 8px; text-align: right; font-weight: bold; width: 13%; white-space: nowrap;">₹${this.formatAmount(this.grandTotal)}</td>
    <td colspan="5" style="background-color: #ffc107 !important; -webkit-print-color-adjust: exact; color-adjust: exact; border: 2px solid #808080; width: 68%;"></td>
  </tr>
</table>
`

          // Create iframe for printing
          const iframe = document.createElement('iframe')
          iframe.style.position = 'absolute'
          iframe.style.top = '-9999px'
          iframe.style.left = '-9999px'
          document.body.appendChild(iframe)

          // Write content to iframe
          iframe.contentDocument.open()
          iframe.contentDocument.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              body {
                margin: 10px;
                font-family: Arial, sans-serif;
              }
              
              table {
                page-break-inside: auto;
              }
              
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              
              th, td {
                page-break-inside: avoid;
              }
              
              /* Prevent orphan rows */
              tbody tr:last-child {
                page-break-after: avoid;
              }
            }
            
            @page {
              margin: 0.4in;
              size: A4 portrait;
            }
            
            body {
              font-family: Arial, sans-serif;
              margin: 10px;
              font-size: 9px;
            }
            
            table {
              font-size: 9px;
            }
          </style>
        </head>
        <body>
          ${summaryHTML}
          ${tableHTML}
          ${grandTotalHTML}
        </body>
        </html>
      `)
          iframe.contentDocument.close()

          setTimeout(() => {
            iframe.contentWindow.print()

            iframe.contentWindow.addEventListener('afterprint', () => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
              }
              resolve('Print completed successfully')
            })

            // Fallback cleanup
            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
                resolve('Print completed')
              }
            }, 500)
          }, 500)
        } catch (error) {
          reject(error)
        }
      })
    },
  },
}
</script>

<style scoped>
.compact-table {
  font-size: 0.875rem;
  table-layout: fixed;
}

.compact-table th,
.compact-table td {
  padding: 0.25rem 0.5rem;
  vertical-align: middle;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.compact-table th {
  font-weight: 600;
  border: 1px solid #808080;
}

/* Borrower group styling - first row of group gets 2px gray top border */
.borrower-group-row:first-child {
  border-top: 2px solid #808080;
}

.borrower-group-row {
  border-top: 1px solid #808080;
}

.borrower-total-row {
  background-color: #ea5455 !important;
  border-top: 1px solid #808080;
  border-bottom: 2px solid #808080 !important;
  font-weight: bold;
  color: white !important;
}

.borrower-total-row td {
  background-color: #ea5455 !important;
  border-top: 1px solid #808080;
  border-bottom: 2px solid #808080;
  color: white !important;
}

.table-footer-highlight {
  background-color: #ffc107 !important;
}

.table-footer-highlight td {
  background-color: #ffc107 !important;
  font-weight: bold;
  border: 2px solid #808080;
}

/* Add border styling to tbody cells */
.compact-table tbody td {
  border: 1px solid #808080;
}

/* Add border styling to thead cells */
.compact-table thead th {
  border: 1px solid #808080;
}

@media print {
  .modal-footer {
    display: none !important;
  }
}
</style>
